import { isEmpty } from "ramda";
import { useEffect, useState } from "react";
import { Avatar, Button, SearchInput, Typography } from "@/components/atoms";
import { OperatorList } from "@/components/organisms";
import { useLayout, useProfile } from "@/hooks";
import { useAppDispatch, useAppResetApi, useAppSelector } from "@/redux/hooks";
import { logOut } from "@/redux/slices/auth/authSlice";
import { operatorsSelector } from "@/redux/slices/operator/selectors";
import { Operator } from "@/redux/slices/operator/types";
import { getAssetUrl } from "@/utils";

export const Operators = () => {
  const dispatch = useAppDispatch();
  const { setFullScreen } = useLayout();
  const { avatar, displayName } = useProfile();
  const resetApi = useAppResetApi();
  const operators = useAppSelector(operatorsSelector);

  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchResult, setSearchResult] = useState<Operator[]>([]);
  const [search, setSearch] = useState<string | undefined>(undefined);

  useEffect(() => {
    setFullScreen(true);
    return () => {
      setFullScreen(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    dispatch(logOut());
    resetApi();
  };

  const handleSearch = (value?: string) => {
    if (value) {
      setSearchResult(operators.filter((operator) => operator.name.toLowerCase().includes(value.toLowerCase())));
    } else setIsSearchActive(false);

    setSearch(value);
  };

  return (
    <div className="flex min-h-screen w-full flex-col items-center bg-[#370a47] px-5 pb-[30px] pt-[100px]">
      <div className="mb-[72px] flex flex-col items-center justify-center">
        <Avatar src={getAssetUrl(avatar)} alt={displayName} size="sm" className="bg-white" />
        <div className="mt-2 flex flex-col items-center text-white">
          <Typography variant="title">Hello, {displayName}</Typography>
          <Typography variant="action" className="opacity-50">
            Administrator
          </Typography>
        </div>
        <Button
          variant="secondary"
          size="sm"
          startIcon="LogoutCurve"
          iconClassName="rotate-180"
          className="mt-7 bg-[#370a47] text-white hover:bg-[#73547e]"
          onClick={handleLogout}
        >
          Logout
        </Button>
      </div>
      {isSearchActive ? (
        <div className="flex flex-col items-center justify-center text-white">
          <Typography variant="title">Search Operator</Typography>
          <SearchInput
            placeholder="Search Operator"
            onSearch={handleSearch}
            search={search}
            className="mt-6 w-[320px] sm:w-[490px] [&_input]:border-[#370a47] [&_input]:bg-[#73547E] [&_input]:text-white"
            autoFocus
          />
          {!isEmpty(searchResult) && (
            <div className="mt-5 flex flex-col items-center justify-center gap-3">
              <Typography variant="paragraph">Results:</Typography>
              <OperatorList operators={searchResult} />
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center text-white">
          <Typography variant="title">Select Operator</Typography>
          <OperatorList operators={operators} />
          <Button
            variant="tertiary"
            size="sm"
            startIcon="SearchNormal1"
            className="mt-12 bg-[#370a47] !text-white hover:bg-[#73547e]"
            onClick={() => setIsSearchActive(true)}
          >
            Search Operator
          </Button>
        </div>
      )}
      <div className="mt-[60px] flex flex-1 items-end">
        <Typography className="text-center text-white opacity-50" variant="small">
          © Copyright {new Date().getFullYear()} - Rideminder Australia Pty Ltd | Powered by RideMinder
        </Typography>
      </div>
    </div>
  );
};
